import React from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { useTheme } from '@emotion/react'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  DELIVERY_FULFILLMENT,
  FulfillmentType,
  PICKUP_FULFILLMENT
} from 'shop/types'
import { useCheckout, useFulfillment, useShop } from 'shop/hooks'
import { getCurrentCartId } from 'shop/utils'
import { getFulfillmentTypeLabel } from 'shop/utils/common'
import ContentLoader from 'react-content-loader'

interface Props {
  currentFulfillmentType: FulfillmentType | undefined
  handleFulfillmentToggleClick: (fulfillmentType: FulfillmentType) => void
  isCartMatch: boolean
  containerHeight?: string
  toggleHeight?: string
}

const generateFulfillmentSearch = (
  fulfillmentType: FulfillmentType
): string => {
  const fulfillmentParam = fulfillmentType.toLowerCase()
  return `?fulfillment_type=${fulfillmentParam}`
}

const FulfillmentToggle: React.FC<Props> = ({
  currentFulfillmentType,
  handleFulfillmentToggleClick,
  isCartMatch,
  containerHeight = '46px',
  toggleHeight = '39px'
}) => {
  const theme = useTheme()
  const { fulfillmentToggleDataLoading } = useCheckout()
  const {
    pickupMinValue,
    deliveryMinValue,
    availableFulfillmentTypes,
    isStoreAcceptingOrders
  } = useFulfillment()
  const { config } = useShop()
  const cartId = getCurrentCartId(config.domain)
  const isSingleFulfillment =
    availableFulfillmentTypes.length === 1 &&
    currentFulfillmentType &&
    availableFulfillmentTypes.includes(currentFulfillmentType)

  // Disable toggle completely if current store is not accepting or user has a cart on another store
  const isDisabled = !isStoreAcceptingOrders || (!isCartMatch && cartId)

  const renderFulfillmentOption = (
    fulfillmentType: FulfillmentType,
    minValue: string
  ) => {
    const href = window.location.pathname
    const search = generateFulfillmentSearch(fulfillmentType)
    const isActive = !isDisabled && fulfillmentType === currentFulfillmentType
    const displayMinValue = isDisabled ? 'Not available' : minValue

    return (
      <a
        key={fulfillmentType}
        href={`${href}${search}`}
        onClick={(e) => {
          e.preventDefault()
          if (!isDisabled) {
            handleFulfillmentToggleClick(fulfillmentType)
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <Option
          data-testid={
            fulfillmentType === DELIVERY_FULFILLMENT
              ? 'fulfillment-toggle-delivery'
              : 'fulfillment-toggle-pickup'
          }
          disabled={fulfillmentToggleDataLoading || isDisabled}
          isActive={isActive}
        >
          {getFulfillmentTypeLabel[fulfillmentType]}
          <MinValue isActive={isActive}>{displayMinValue}</MinValue>
        </Option>
      </a>
    )
  }

  if (isSingleFulfillment) {
    const minValue =
      currentFulfillmentType === PICKUP_FULFILLMENT
        ? pickupMinValue
        : deliveryMinValue
    const label =
      currentFulfillmentType === PICKUP_FULFILLMENT ? 'Pickup' : 'Delivery'

    return (
      <SingleFulfillmentContainer data-testid='fulfillment-single'>
        <SingleFulfillmentText>{label}</SingleFulfillmentText>
        <SingleFulfillmentMinValue>{minValue}</SingleFulfillmentMinValue>
      </SingleFulfillmentContainer>
    )
  }

  // Show loader if fulfillment toggle data is loading
  if (fulfillmentToggleDataLoading) {
    return (
      <ContentLoader
        data-testid='fulfillment-toggle-loader'
        speed={2}
        style={{
          width: '100%',
          height: containerHeight,
          alignSelf: 'center',
          borderRadius: theme.borderRadius,
          display: 'block'
        }}
      />
    )
  }

  return (
    <Container
      data-testid='fulfillment-toggle'
      isStoreAcceptingOrders={!isDisabled}
      containerHeight={containerHeight}
    >
      {!isDisabled && currentFulfillmentType && (
        <Toggle
          fulfillmentType={currentFulfillmentType}
          toggleHeight={toggleHeight}
        />
      )}
      {renderFulfillmentOption(DELIVERY_FULFILLMENT, deliveryMinValue)}
      {renderFulfillmentOption(PICKUP_FULFILLMENT, pickupMinValue)}
    </Container>
  )
}

const baseFulfillmentTextStyles = {
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.1
}

const Container = styled.div<
  StyledHTMLElement & {
    isStoreAcceptingOrders: boolean
    containerHeight: string
  }
>(
  ({ theme, isStoreAcceptingOrders = true, containerHeight }): CSSObject => ({
    display: 'flex',
    borderRadius: theme.borderRadius,
    backgroundColor: isStoreAcceptingOrders ? '#F5F5F5' : theme.colors.white,
    opacity: isStoreAcceptingOrders ? 1 : 0.4,
    position: 'relative',
    alignItems: 'center',
    width: '100%',
    height: containerHeight,
    alignSelf: 'center'
  })
)

const Toggle = styled.div<
  StyledHTMLElement & { fulfillmentType: FulfillmentType; toggleHeight: string }
>(({ fulfillmentType, theme, toggleHeight }) => ({
  position: 'absolute',
  top: '50%',
  left: '3.5px',
  width: 'calc(50% - 7px)',
  height: toggleHeight,
  backgroundColor: theme.colors.primary,
  color: theme.colors.white,
  borderRadius: theme.borderRadius,
  transition: 'transform 0.3s ease',
  transform:
    fulfillmentType === DELIVERY_FULFILLMENT
      ? 'translateX(0) translateY(-50%)'
      : 'translateX(calc(100% + 7px)) translateY(-50%)'
}))

const Option = styled.div<
  StyledHTMLElement & { disabled?: boolean; isActive?: boolean }
>(
  ({ disabled, isActive, theme }): CSSObject => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2,
    cursor: disabled ? 'default' : 'pointer',
    height: '100%',
    color: isActive ? theme.colors.white : 'inherit',
    ...baseFulfillmentTextStyles
  })
)

const MinValue = styled.p<StyledHTMLElement & { isActive?: boolean }>(
  ({ isActive, theme }) => ({
    fontSize: '12px',
    color: isActive ? theme.colors.white : '#59595A',
    margin: 0
  })
)

const SingleFulfillmentContainer = styled.div<StyledHTMLElement>(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    width: '100%',
    height: '46px',
    alignSelf: 'center'
  })
)

const SingleFulfillmentText = styled.div<StyledHTMLElement>(() => ({
  color: 'black',
  ...baseFulfillmentTextStyles
}))

const SingleFulfillmentMinValue = styled.p<StyledHTMLElement>(() => ({
  fontSize: '12px',
  color: '#59595A',
  margin: 0
}))

export default FulfillmentToggle
